import { useCallback, useEffect, useRef, useState } from "react";
import { Ban, Paperclip, Reply, Send, X, FileText } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn, bytesHuman, messagePreview } from "@/lib/utils";
import { useDrafts } from "@/store/drafts";
import { useOutbox } from "@/store/outbox";
import type { MediaOut, Message } from "@/lib/types";

interface Props {
  accountId: string;
  chatId: string;
  replyTo?: Message | null;
  replyLabel?: string;
  onCancelReply?: () => void;
  /** Причина, по которой в чат нельзя писать ("blocked" | "deactivated"), либо null. */
  blocked?: string | null;
}

/** Одно вложение композера: от выбора файла до готового MediaOut с сервера. */
interface Attachment {
  id: string;
  file: File;
  media: MediaOut | null;
  previewUrl: string | null;
  uploading: boolean;
  pct: number;
  error: string | null;
}

function localId(): string {
  return `local:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
}

export function Composer({ accountId, chatId, replyTo, replyLabel, onCancelReply, blocked }: Props) {
  const { push } = useToast();
  const draft = useDrafts((s) => s.byChat[chatId] ?? "");
  const [text, setText] = useState(draft);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Троттлинг живого typing-индикатора: шлём не чаще раза в ~4с, пока
  // оператор реально печатает (Telegram сам гасит статус через несколько
  // секунд бездействия — явный "cancel" не нужен).
  const typingLastSentRef = useRef(0);

  // Черновик подтягиваем при смене чата и сохраняем с задержкой: писать в
  // localStorage на каждое нажатие клавиши незачем.
  useEffect(() => {
    setText(useDrafts.getState().byChat[chatId] ?? "");
    typingLastSentRef.current = 0;
  }, [chatId]);

  useEffect(() => {
    const t = window.setTimeout(() => useDrafts.getState().set(chatId, text), 300);
    return () => window.clearTimeout(t);
  }, [chatId, text]);

  /**
   * Отправка оптимистична: пузырь появляется сразу, а воркер шлёт без
   * искусственной задержки. Реальное сообщение подменит его, когда придёт
   * message.outgoing с тем же command_id (см. useLiveCache).
   */
  const [sending, setSending] = useState(false);

  // Превью — blob URL живёт только в браузере; ref всегда содержит актуальный
  // список вложений, чтобы при размонтировании освободить все разом.
  const attachmentsRef = useRef<Attachment[]>([]);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);
  useEffect(() => {
    return () => {
      for (const a of attachmentsRef.current) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    };
  }, []);

  function onTextChange(v: string) {
    setText(v);
    if (blocked || !v.trim()) return;
    const now = Date.now();
    if (now - typingLastSentRef.current < 4000) return;
    typingLastSentRef.current = now;
    api.setTyping(accountId, chatId).catch(() => {});
  }

  const uploadAttachment = useCallback(
    async (id: string, file: File, kind?: string) => {
      setAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, uploading: true, pct: 0, error: null } : a))
      );
      try {
        const m = await api.uploadMedia(
          file,
          (pct) => setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, pct } : a))),
          kind
        );
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, media: m, uploading: false } : a)));
      } catch (e) {
        const msg = (e as ApiError).message ?? String(e);
        push({ title: "Ошибка загрузки", description: msg, variant: "destructive" });
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, uploading: false, error: msg } : a)));
      }
    },
    [push]
  );

  const handleFiles = useCallback(
    (files: File[]) => {
      const next: Attachment[] = files.map((f) => ({
        id: `${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        media: null,
        previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        uploading: true,
        pct: 0,
        error: null,
      }));
      setAttachments((prev) => [...prev, ...next]);
      for (const a of next) void uploadAttachment(a.id, a.file);
    },
    [uploadAttachment]
  );

  function removeAttachment(id: string) {
    setAttachments((prev) => {
      const a = prev.find((x) => x.id === id);
      if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  }

  /** Отправка одного сообщения (текст и/или одно медиа) — оптимистичный пузырь
   *  в outbox, реальная отправка через API; повторно используется и для чисто
   *  текстовых сообщений, и для «россыпи» разнородных вложений по одному. */
  async function sendSingle(
    itemText: string | null,
    mediaId: string | null,
    fileName: string | null,
    mediaKind: string | null,
    replyToId: number | null
  ) {
    const id = localId();
    useOutbox.getState().add({
      localId: id,
      accountId,
      chatId,
      text: itemText,
      fileName,
      mediaKind,
      replyToTgId: replyToId,
    });
    try {
      const res = await api.sendMessage(accountId, chatId, {
        text: itemText,
        media_id: mediaId ?? undefined,
        reply_to: replyToId,
      });
      useOutbox.getState().attachCommandId(id, res.command_id);
    } catch (e) {
      const msg = (e as ApiError).message ?? String(e);
      useOutbox.getState().fail(id, msg);
      push({ title: "Не отправилось", description: msg, variant: "destructive" });
    }
  }

  async function submit() {
    const trimmedText = text.trim() || null;
    const replyToId = replyTo?.tg_message_id ?? null;
    const ready = attachments.filter((a) => a.media);

    setText("");
    useDrafts.getState().clear(chatId);
    for (const a of attachments) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    setAttachments([]);
    onCancelReply?.();

    setSending(true);
    try {
      const isAlbum =
        ready.length >= 2 && ready.every((a) => a.media!.kind === "photo" || a.media!.kind === "video");

      if (isAlbum) {
        // Несколько фото/видео разом уходят одним альбомом (Telegram grouped media).
        const id = localId();
        useOutbox.getState().add({
          localId: id,
          accountId,
          chatId,
          text: trimmedText,
          fileName: `${ready.length} вложений`,
          mediaKind: "album",
          replyToTgId: replyToId,
        });
        try {
          const res = await api.sendMessage(accountId, chatId, {
            text: trimmedText,
            media_ids: ready.map((a) => a.media!.id),
            reply_to: replyToId,
          });
          useOutbox.getState().attachCommandId(id, res.command_id);
        } catch (e) {
          const msg = (e as ApiError).message ?? String(e);
          useOutbox.getState().fail(id, msg);
          push({ title: "Не отправилось", description: msg, variant: "destructive" });
        }
      } else if (ready.length >= 1) {
        // Одиночное вложение или разнородный набор (голосовое/документ и т.п.) —
        // Telegram такое не альбомит, шлём по одному сообщению на файл.
        for (let i = 0; i < ready.length; i++) {
          const a = ready[i];
          await sendSingle(
            i === 0 ? trimmedText : null,
            a.media!.id,
            a.file.name,
            a.media!.kind,
            replyToId
          );
        }
      } else {
        await sendSingle(trimmedText, null, null, null, replyToId);
      }
    } finally {
      setSending(false);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) handleFiles(files);
  }

  /** Скриншот(ы) из буфера уходят вложением — не через «сохранить файл → выбрать». */
  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(e.clipboardData.files);
    if (!files.length) return;
    e.preventDefault();
    handleFiles(files);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (canSend) submit();
    }
    if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      onCancelReply?.();
    }
  }

  const canSend =
    !blocked &&
    !sending &&
    attachments.every((a) => !a.uploading) &&
    (text.trim().length > 0 || attachments.some((a) => a.media));

  return (
    <div
      className={cn(
        "relative border-t border-border bg-card/40 p-3 transition-colors",
        dragOver && "bg-primary/10"
      )}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-2 rounded-md border-2 border-dashed border-primary/60 bg-primary/5 flex items-center justify-center text-sm text-primary">
          Брось файлы сюда
        </div>
      )}

      {blocked && (
        <div className="mb-2 flex items-center gap-2 rounded-md border-l-2 border-destructive bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
          <Ban className="h-4 w-4 shrink-0" />
          {blocked === "deactivated"
            ? "Собеседник удалил аккаунт — сообщения не доставляются."
            : "Собеседник заблокировал этот аккаунт — сообщения не доставляются."}
        </div>
      )}

      {replyTo && (
        <div className="mb-2 flex items-center gap-2 rounded-md border-l-2 border-primary bg-primary/5 px-2 py-1.5">
          <Reply className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium text-primary">
              Ответ · {replyLabel ?? `#${replyTo.tg_message_id}`}
            </div>
            <div className="truncate text-xs text-muted-foreground">{messagePreview(replyTo)}</div>
          </div>
          <Button variant="ghost" size="iconSm" onClick={() => onCancelReply?.()} title="Отменить ответ">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
          {attachments.map((a) => (
            <AttachmentChip
              key={a.id}
              attachment={a}
              onRemove={() => removeAttachment(a.id)}
              onKindChange={
                attachments.length === 1 ? (kind) => uploadAttachment(a.id, a.file, kind) : undefined
              }
            />
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) handleFiles(files);
            e.target.value = "";
          }}
        />
        <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} title="Прикрепить файлы">
          <Paperclip className="h-5 w-5" />
        </Button>

        <Textarea
          ref={taRef}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder="Сообщение… (⌘/Ctrl + Enter — отправить)"
          rows={1}
          className="max-h-40 min-h-9 py-2"
        />

        <Button onClick={() => submit()} disabled={!canSend} title="Отправить (⌘/Ctrl + Enter)">
          <Send className="h-4 w-4" />
          Отправить
        </Button>
      </div>
    </div>
  );
}

/** Миниатюра одного вложения в композере: превью/иконка, прогресс загрузки и,
 *  для одиночного аудио/видео, переключатель «как отправить» (голосовое/
 *  аудио, видео/кружок) — Telegram определяет это по способу записи, а не
 *  только по MIME, поэтому даём выбрать явно. */
function AttachmentChip({
  attachment,
  onRemove,
  onKindChange,
}: {
  attachment: Attachment;
  onRemove: () => void;
  onKindChange?: (kind: string) => void;
}) {
  const { file, media, previewUrl, uploading, pct, error } = attachment;
  const baseKind = file.type.startsWith("audio/") ? "audio" : file.type.startsWith("video/") ? "video" : null;

  return (
    <div className="flex w-44 shrink-0 flex-col gap-1.5 rounded-md border border-border bg-card p-2">
      <div className="flex items-center gap-2">
        <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-secondary text-muted-foreground">
          {previewUrl ? (
            <img src={previewUrl} alt={file.name} className="h-full w-full object-cover" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">{file.name}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {bytesHuman(file.size)}
            {uploading && ` · ${pct}%`}
            {media && !uploading && <span className="text-[hsl(var(--success))]"> · готово</span>}
            {error && <span className="text-destructive"> · ошибка</span>}
          </div>
        </div>
        <Button variant="ghost" size="iconSm" onClick={onRemove}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {uploading && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}
      {onKindChange && baseKind === "audio" && (
        <div className="flex gap-1">
          <KindPill disabled={uploading} active={media?.kind === "voice"} onClick={() => onKindChange("voice")}>
            Голосовое
          </KindPill>
          <KindPill disabled={uploading} active={media?.kind === "audio"} onClick={() => onKindChange("audio")}>
            Аудио
          </KindPill>
        </div>
      )}
      {onKindChange && baseKind === "video" && (
        <div className="flex gap-1">
          <KindPill disabled={uploading} active={media?.kind === "video"} onClick={() => onKindChange("video")}>
            Видео
          </KindPill>
          <KindPill
            disabled={uploading}
            active={media?.kind === "video_note"}
            onClick={() => onKindChange("video_note")}
          >
            Кружок
          </KindPill>
        </div>
      )}
    </div>
  );
}

function KindPill({
  active,
  disabled,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"
      )}
    >
      {children}
    </button>
  );
}
